import React from "react";
import { HomeworkHistoryAssignment } from "../api/contracts";
import { formatPortalDate } from "../utils/dateTime";

export interface SudsTrendPoint {
  date: string;
  before: number | null;
  after: number | null;
  beforeCount: number;
  afterCount: number;
}

function completedDateKey(value: string | null): string | null {
  const match = value?.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? `${match[1]}-${match[2]}-${match[3]}`
    : null;
}

function validSuds(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value >= 0 && value <= 10;
}

function average(values: readonly number[]): number | null {
  if (!values.length) return null;
  const result = values.reduce((total, value) => total + value, 0) / values.length;
  return Math.round(result * 10) / 10;
}

export function buildSudsTrendPoints(
  assignments: readonly HomeworkHistoryAssignment[],
): SudsTrendPoint[] {
  const groups = new Map<string, { before: number[]; after: number[] }>();

  for (const assignment of assignments) {
    const date = completedDateKey(assignment.completedAt);
    if (!date) continue;

    const before = validSuds(assignment.firstSuds)
      ? assignment.firstSuds
      : null;
    const after = validSuds(assignment.secondSuds)
      ? assignment.secondSuds
      : null;
    if (before === null && after === null) continue;

    const group = groups.get(date) ?? { before: [], after: [] };
    if (before !== null) group.before.push(before);
    if (after !== null) group.after.push(after);
    groups.set(date, group);
  }

  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, group]) => ({
      date,
      before: average(group.before),
      after: average(group.after),
      beforeCount: group.before.length,
      afterCount: group.after.length,
    }));
}

function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function SudsTrendChart({ points }: { points: readonly SudsTrendPoint[] }) {
  if (!points.length) {
    return (
      <div className="cp-chart-empty">
        <h3>No SUDS trend data yet</h3>
        <p>
          Completed homework with pre- or post-exposure SUDS will appear here.
        </p>
      </div>
    );
  }

  const width = 760;
  const height = 340;
  const margin = { top: 24, right: 24, bottom: 62, left: 52 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const xAt = (index: number) =>
    margin.left +
    (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const yAt = (value: number) => margin.top + ((10 - value) / 10) * plotHeight;
  const labelEvery = Math.max(1, Math.ceil(points.length / 8));

  const pathFor = (key: "before" | "after") =>
    points
      .map((point, index) =>
        point[key] === null ? null : `${xAt(index)},${yAt(point[key])}`,
      )
      .filter((value): value is string => Boolean(value))
      .map((coordinate, index) => `${index === 0 ? "M" : "L"}${coordinate}`)
      .join(" ");

  return (
    <figure className="cp-suds-chart">
      <div className="cp-suds-chart__plot">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Average SUDS before and after homework by completion date"
        >
          {[0, 2, 4, 6, 8, 10].map((value) => {
            const y = yAt(value);
            return (
              <g key={value}>
                <line
                  className="cp-suds-chart__grid"
                  x1={margin.left}
                  x2={width - margin.right}
                  y1={y}
                  y2={y}
                />
                <text
                  className="cp-suds-chart__axis-label"
                  x={margin.left - 12}
                  y={y + 4}
                  textAnchor="end"
                >
                  {value}
                </text>
              </g>
            );
          })}

          <line
            className="cp-suds-chart__axis"
            x1={margin.left}
            x2={margin.left}
            y1={margin.top}
            y2={height - margin.bottom}
          />
          <line
            className="cp-suds-chart__axis"
            x1={margin.left}
            x2={width - margin.right}
            y1={height - margin.bottom}
            y2={height - margin.bottom}
          />

          {points.map((point, index) =>
            index % labelEvery === 0 || index === points.length - 1 ? (
              <text
                className="cp-suds-chart__axis-label"
                key={point.date}
                x={xAt(index)}
                y={height - margin.bottom + 24}
                textAnchor="middle"
              >
                {formatPortalDate(point.date)}
              </text>
            ) : null,
          )}

          <text
            className="cp-suds-chart__axis-title"
            x={14}
            y={margin.top + plotHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`}
          >
            Average SUDS
          </text>
          <text
            className="cp-suds-chart__axis-title"
            x={margin.left + plotWidth / 2}
            y={height - 12}
            textAnchor="middle"
          >
            Completed Date
          </text>

          {pathFor("before") ? (
            <path
              className="cp-suds-chart__line"
              data-series="before"
              d={pathFor("before")}
            />
          ) : null}
          {pathFor("after") ? (
            <path
              className="cp-suds-chart__line"
              data-series="after"
              d={pathFor("after")}
            />
          ) : null}

          {points.flatMap((point, index) =>
            (["before", "after"] as const).flatMap((series) => {
              const value = point[series];
              if (value === null) return [];
              const seriesLabel = series === "before" ? "Before" : "After";
              return [
                <circle
                  className="cp-suds-chart__dot"
                  data-series={series}
                  key={`${point.date}-${series}`}
                  cx={xAt(index)}
                  cy={yAt(value)}
                  r={5}
                  tabIndex={0}
                  aria-label={`${seriesLabel} average ${formatValue(value)} on ${formatPortalDate(point.date)}`}
                >
                  <title>
                    {seriesLabel}: {formatValue(value)} on {formatPortalDate(point.date)}
                  </title>
                </circle>,
              ];
            }),
          )}
        </svg>
      </div>
      <figcaption className="cp-suds-chart__legend">
        <span data-series="before">
          <i aria-hidden="true" /> Before (Pre-Exposure)
        </span>
        <span data-series="after">
          <i aria-hidden="true" /> After (Post-Exposure)
        </span>
      </figcaption>
    </figure>
  );
}
